import { useCallback } from 'react';

import { getFallbackPrice } from '@/data/pricing-fallbacks';
import { usePricingOptions } from '@/hooks/api/usePricingOptions';
import { useCurrency } from '@/hooks/billing/useCurrency';
import { planData } from '@/plan-data';

import type { FallbackPlanKey } from '@/data/pricing-fallbacks';
import type { PricingByCurrency } from '@/hooks/api/usePricingOptions';

/** Plans the usage step can send to checkout (`/subscribe?plan=<slug>`). */
export type CheckoutPlanSlug = 'essentials' | 'creator' | 'pro' | 'business';

const MONTHS_PER_YEAR = 12;
const TWO_DECIMALS = 100;

const roundToMonthly = (yearly: number) =>
  Math.round((yearly / MONTHS_PER_YEAR) * TWO_DECIMALS) / TWO_DECIMALS;

/**
 * "From" prices for the usage step's plan cards: the monthly equivalent of
 * each plan's yearly price (the cheapest per-month rate), formatted to two
 * decimals. Prefers live `/admin/pricing` data for the visitor's currency,
 * then the static fallbacks — mirroring `usePricingCards`' yearly-mode
 * maths. Business isn't sold via the pricing API, so it resolves through
 * the legacy static plan data.
 */
export const usePlanPrices = () => {
  const currency = useCurrency();
  const { data: pricingOptions } = usePricingOptions();

  const currencyCode = currency?.code?.toLowerCase() ?? 'usd';
  const symbol = currency?.symbol_left ?? '$';

  const priceFor = useCallback(
    (plan: CheckoutPlanSlug): string => {
      const option = pricingOptions?.find(
        (candidate) => candidate.title.toLowerCase() === plan
      );
      const pricing =
        option?.pricing?.[currencyCode as keyof PricingByCurrency];
      if (pricing?.yearly) {
        return roundToMonthly(pricing.yearly).toFixed(2);
      }

      if (plan === 'business') {
        // The static map covers our supported currencies; anything else
        // falls back to USD, mirroring getFallbackPrice — never render
        // "From $0.00/month".
        const businessPrices: Partial<Record<string, string>> =
          planData.business.monthly_equivalent;
        const fallback = Number(
          businessPrices[currencyCode.toUpperCase()] ??
            planData.business.monthly_equivalent.USD
        );
        return fallback.toFixed(2);
      }

      return roundToMonthly(
        getFallbackPrice(plan as FallbackPlanKey, currencyCode, 'yearly')
      ).toFixed(2);
    },
    [pricingOptions, currencyCode]
  );

  return { symbol, priceFor };
};
